/**
 * Web-only singleton for window/viewport resize.
 * One listener; components subscribe/unsubscribe to avoid multiple listeners.
 */

const DEBOUNCE_MS = 200;

type Listener = () => void;

let rafId: number | null = null;
let lastRun = 0;
const listeners = new Set<Listener>();
let windowListenerAttached = false;
let viewportListenerAttached = false;

function getSize() {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  const vp = window.visualViewport;
  return {
    width: vp?.width ?? window.innerWidth,
    height: vp?.height ?? window.innerHeight,
  };
}

function notify() {
  const now = Date.now();
  if (now - lastRun < DEBOUNCE_MS) {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      lastRun = Date.now();
      listeners.forEach((fn) => {
        try {
          fn();
        } catch (_) {}
      });
    });
    return;
  }
  lastRun = now;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {}
  });
}

function attach() {
  if (typeof window === 'undefined') return;
  if (window.visualViewport && !viewportListenerAttached) {
    window.visualViewport.addEventListener('resize', notify);
    viewportListenerAttached = true;
  }
  if (!windowListenerAttached) {
    window.addEventListener('resize', notify);
    windowListenerAttached = true;
  }
}

function detach() {
  if (typeof window === 'undefined') return;
  if (viewportListenerAttached && window.visualViewport) {
    window.visualViewport.removeEventListener('resize', notify);
    viewportListenerAttached = false;
  }
  if (windowListenerAttached) {
    window.removeEventListener('resize', notify);
    windowListenerAttached = false;
  }
}

export function subscribeResize(callback: Listener): () => void {
  listeners.add(callback);
  if (listeners.size === 1) attach();
  return () => {
    listeners.delete(callback);
    if (listeners.size === 0) detach();
  };
}

export function getWindowSize(): { width: number; height: number } {
  return getSize();
}
